// src/pages/LandingPage.js
// Landing Page — Screenshot-Driven Conversion Overhaul (PRD Website Conversion S2)

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';
import SEOHead from '../components/common/SEOHead';
import { ScreenshotFrame, Reveal, PhaseFlow, PublicHeader, PublicFooter } from '../components/common';
import { ENTERPRISE_SIZING_MAILTO } from '../utils/enterpriseContact';
import './LandingPage.css';

function LandingPage() {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const isDevelopment = process.env.NODE_ENV === 'development';
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleTrial = () => {
    if (isDevelopment) {
      navigate('/command-center');
    } else {
      instance.loginRedirect({
        ...loginRequest,
        prompt: 'create',
      });
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqData = [
    {
      question: "What does 'continuous monitoring' mean?",
      answer: "When you add a domain or IP range as an asset, we automatically scan it on a regular schedule — daily for vulnerability scans, weekly for deep assessments. You get notified whenever something changes: new subdomains, open ports, vulnerabilities, or misconfigurations."
    },
    {
      question: "Do I need to be technical to use this?",
      answer: "No. Results include plain-English explanations and prioritized recommendations. You'll know what to fix first and why it matters."
    },
    {
      question: "What exactly do you monitor?",
      answer: "We monitor from the outside, like an attacker would — DNS records, subdomains, open ports, web services, SSL certificates, and known vulnerabilities. We never access your internal systems."
    },
    {
      question: "How is this different from expensive enterprise tools?",
      answer: "Enterprise EASM platforms like Detectify, UpGuard, or Censys cost $50K-$500K/year and require dedicated security staff. DriftAlarm gives small businesses the same visibility with plans starting at $99/month — no sales process, no 6-month onboarding."
    },
    {
      question: "What makes DriftAlarm different from Detectify, UpGuard, or Censys?",
      answer: "Enterprise EASM tools start at $50K/year and require dedicated security teams. DriftAlarm gives you the same attack surface visibility — including AI-powered analysis, MITRE ATT&CK mapping, and drift detection — starting at $99/month with no sales process. We're built for IT teams of 1-5 people who need enterprise-grade visibility without enterprise complexity."
    },
    {
      question: "What is drift detection?",
      answer: "Configuration drift is when your external assets change from their expected state — a new port opens, a certificate expires, a DNS record changes, a new subdomain appears. DriftAlarm monitors 41 different drift conditions across 8 categories (certificates, DNS, network, compliance, and more) and alerts you the moment something shifts. Most breaches exploit drift that went unnoticed for weeks or months."
    }
  ];

  // Structured data for SEO
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "DriftAlarm",
    "legalName": "DriftAlarm",
    "url": "https://www.driftalarm.com",
    "logo": "https://www.driftalarm.com/images/og-image.png",
    "image": "https://www.driftalarm.com/images/screenshots/command-center.png",
    "description": "Continuous external attack surface monitoring with AI-powered drift detection and remediation guidance",
    "foundingDate": "2025",
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "support@driftalarm.com",
      "contactType": "customer support"
    },
    "sameAs": []
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "DriftAlarm",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Web",
    "screenshot": "https://www.driftalarm.com/images/screenshots/command-center.png",
    "offers": [
      {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "name": "30-Day Trial",
        "description": "Full-featured 30-day trial at Standard tier limits",
        "availability": "https://schema.org/InStock"
      },
      {
        "@type": "Offer",
        "price": "99",
        "priceCurrency": "USD",
        "name": "Standard",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "99",
          "priceCurrency": "USD",
          "unitText": "MONTH"
        }
      },
      {
        "@type": "Offer",
        "price": "389",
        "priceCurrency": "USD",
        "name": "Pro",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "389",
          "priceCurrency": "USD",
          "unitText": "MONTH"
        }
      }
    ],
    "description": "Continuous external attack surface monitoring with AI-powered drift detection. Discover subdomains, open ports, and vulnerabilities across your domains and IPs — with 41 drift detection rules and AI remediation guidance."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home" }
    ]
  };

  return (
    <div className="landing-container">
      <SEOHead
        title="DriftAlarm — AI-Enabled External Attack Surface Assessments. Know Your Baseline."
        description="Your attack surface changed today. New subdomains, expired certificates, open ports, configuration drift. DriftAlarm monitors 24/7 and alerts you the moment something shifts. AI-powered remediation guidance. Plans from $99/mo."
        path="/"
        jsonLd={[organizationSchema, softwareSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Animated Ocean Background */}
      <div className="ocean-bg">
        <div className="wave wave-1"></div>
        <div className="wave wave-2"></div>
        <div className="wave wave-3"></div>
      </div>

      {/* ===== NAVIGATION BAR (unified) ===== */}
      <PublicHeader activeLink={null} />

      {/* ===== 1. HERO SECTION ===== */}
      <section className="hero-section">
        <div className="hero-content hero-split">
          <div className="hero-copy">
            {/* PRD-198 WP3 — time-boxed advisory link; remove once PHP2Shell is no longer news. */}
            <Link to="/guides/php2shell-cve-2026-109618-109619" className="hero-advisory">
              <span className="hero-advisory-tag">Advisory</span>
              <span className="hero-advisory-text">PHP2Shell (CVE-2026-109618 / 109619): check and patch your PHP servers</span>
              <span className="hero-advisory-arrow" aria-hidden="true">→</span>
            </Link>
            <h1 className="hero-title">
              AI-enabled external security assessments
            </h1>
            <p className="hero-subhead">
              Know your baseline.
              <br />
              <span className="hero-subhead-accent">Get notified when it changes.</span>
            </p>
            <p className="hero-description">
              New subdomains. Old dev servers. Exposed services. Configuration drift.
              Things happen. DriftAlarm maps and monitors your external attack surface
              24/7 and alerts you to risks that need your attention.
            </p>
            <div className="hero-cta-group">
              <button className="da-btn da-btn--primary" onClick={handleTrial}>
                <span className="cta-text cta-shiny-text">Start Your Free 30-Day Trial</span>
                <span className="cta-icon">→</span>
              </button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">
                <span className="cta-text">See How It Works</span>
              </Link>
            </div>
            <p className="hero-trust-strip">
              No credit card required &nbsp;&bull;&nbsp; First scan in under 90 seconds &nbsp;&bull;&nbsp; Plans from $99/month
            </p>
          </div>
          <div className="hero-screenshot">
            <ScreenshotFrame
              src="/images/screenshots/command-center-v2"
              alt="DriftAlarm Command Center — risk score trends, vulnerability breakdown, and drift events dashboard"
              size="full"
              lazy={false}
              retina
            />
          </div>
        </div>
      </section>

      {/* ===== 2. PHASE FLOW (deep-links into /how-it-works) ===== */}
      <PhaseFlow linkBase="/how-it-works" />

      {/* ===== 3. WHAT YOU GET (screenshot cards) ===== */}
      <section className="landing-features-section">
        <h2 className="section-title">Everything You Need to Defend Your Attack Surface</h2>
        <p className="section-subtitle">One platform. Complete visibility. Actionable intelligence.</p>

        <div className="landing-features-grid">
          <Reveal className="landing-feature-card" delay={0}>
            <ScreenshotFrame
              src="/images/screenshots/command-center-v2"
              alt="DriftAlarm unified security dashboard with risk scores and vulnerability trends"
              size="full"
              caption="Security Command Center"
              retina
            />
            <h3 className="landing-feature-title">Unified Security Dashboard</h3>
            <p className="landing-feature-desc">Risk scores, vulnerability trends, and drift events — all in one view. Know your security posture at a glance, track improvement over time.</p>
          </Reveal>
          <Reveal className="landing-feature-card" delay={80}>
            <ScreenshotFrame
              src="/images/screenshots/security-intelligence"
              alt="AI-powered security brief with threat level, risk score, and prioritized actions"
              size="full"
              caption="AI Security Intelligence"
            />
            <h3 className="landing-feature-title">AI-Powered Threat Analysis</h3>
            <p className="landing-feature-desc">Every scan produces an AI-generated security brief with threat level, attack complexity, prioritized actions, and plain-English executive summary.</p>
          </Reveal>
          <Reveal className="landing-feature-card" delay={160}>
            <ScreenshotFrame
              src="/images/screenshots/drift-alarm-rules"
              alt="41 drift detection rules across 8 categories with toggle controls and trigger counts"
              size="full"
              caption="Drift Detection Rules"
            />
            <h3 className="landing-feature-title">41 Rules. 8 Categories. Zero Blind Spots.</h3>
            <p className="landing-feature-desc">Certificate changes, DNS drift, new ports, technology updates, compliance gaps — monitored automatically. Get alerted the moment something changes.</p>
          </Reveal>
          <Reveal className="landing-feature-card" delay={240}>
            <ScreenshotFrame
              src="/images/screenshots/prioritized-recommendations"
              alt="AI-ranked remediation recommendations with severity, effort, and timeline"
              size="full"
              caption="Prioritized Remediation"
            />
            <h3 className="landing-feature-title">Know Exactly What to Fix First</h3>
            <p className="landing-feature-desc">AI-ranked recommendations with severity, effort estimate, attack vector, and specific fix instructions. No more guessing which vulnerability matters most.</p>
          </Reveal>
        </div>
      </section>

      {/* ===== 4. HOW IT WORKS (3 steps) ===== */}
      <section className="how-it-works-section">
        <h2 className="section-title">Get Started in Three Steps</h2>
        <div className="steps-container">
          <Reveal className="step" delay={0}>
            <div className="step-number">1</div>
            <div className="step-content">
              <h3 className="step-title">Add Your Domains & IPs</h3>
              <p className="step-description">
                Enter the domains and IP ranges you want to monitor. Our scanner immediately maps your external attack surface — subdomains, open ports, services, and technologies across your entire perimeter.
              </p>
              <p className="step-proof">"We discovered 60 live subdomains and 90 open ports across 45 IP addresses in minutes."</p>
            </div>
          </Reveal>
          <div className="step-connector"></div>
          <Reveal className="step" delay={120}>
            <div className="step-number">2</div>
            <div className="step-content">
              <h3 className="step-title">Automated Scanning & Drift Detection</h3>
              <p className="step-description">
                Daily vulnerability scans and weekly deep assessments run automatically. 41 drift detection rules watch for certificate changes, DNS modifications, new services, and compliance gaps — alerting you the moment something shifts.
              </p>
              <p className="step-proof">"A critical-severity drift event was caught and I was notified automatically."</p>
            </div>
          </Reveal>
          <div className="step-connector"></div>
          <Reveal className="step" delay={240}>
            <div className="step-number">3</div>
            <div className="step-content">
              <h3 className="step-title">Fix What Matters First</h3>
              <p className="step-description">
                Every finding includes AI-generated remediation guidance ranked by severity, effort, and business impact. MITRE ATT&CK mapping shows how attackers could chain vulnerabilities. Compliance mapping connects findings to OWASP, CIS, and NIST frameworks.
              </p>
              <p className="step-proof">"From scan to prioritized fix list in 90 seconds."</p>
            </div>
          </Reveal>
        </div>
        <p className="steps-tagline">Set it up once. We watch continuously.</p>
      </section>

      {/* ===== 5. WHY DRIFTALARM (comparison) ===== */}
      <section className="landing-compare-section">
        <h2 className="section-title">Stop Overpaying. Stop Guessing. Start Knowing.</h2>

        <div className="landing-compare-grid">
          <Reveal className="landing-compare-card landing-compare-risk" delay={0}>
            <h3 className="landing-compare-heading">Doing Nothing</h3>
            <ul className="landing-compare-list">
              <li className="landing-compare-item negative">No visibility into what's internet-facing</li>
              <li className="landing-compare-item negative">Vulnerabilities found by attackers first</li>
              <li className="landing-compare-item negative">Configuration drift goes unnoticed for months</li>
              <li className="landing-compare-item negative">Can't answer "are we secure?" when the board asks</li>
              <li className="landing-compare-item negative">Compliance gaps discovered at audit time</li>
            </ul>
            <div className="landing-compare-price">
              <span className="landing-compare-price-tag negative">Average breach cost: $4.45M</span>
            </div>
          </Reveal>

          <Reveal className="landing-compare-card landing-compare-enterprise" delay={120}>
            <h3 className="landing-compare-heading">Enterprise EASM</h3>
            <ul className="landing-compare-list">
              <li className="landing-compare-item neutral">Full attack surface discovery</li>
              <li className="landing-compare-item neutral">Continuous monitoring</li>
              <li className="landing-compare-item neutral">Vulnerability detection</li>
              <li className="landing-compare-item negative">6-month onboarding with professional services</li>
              <li className="landing-compare-item negative">Requires dedicated security team to operate</li>
              <li className="landing-compare-item negative">Locked into vendor ecosystem</li>
            </ul>
            <div className="landing-compare-price">
              <span className="landing-compare-price-tag neutral">$50,000 – $500,000/year</span>
            </div>
          </Reveal>

          <Reveal className="landing-compare-card landing-compare-driftalarm" delay={240}>
            <h3 className="landing-compare-heading">DriftAlarm</h3>
            <ul className="landing-compare-list">
              <li className="landing-compare-item positive">Full attack surface discovery</li>
              <li className="landing-compare-item positive">AI-powered drift detection (41 rules, 8 categories)</li>
              <li className="landing-compare-item positive">MITRE ATT&CK + OWASP + CIS + NIST mapping</li>
              <li className="landing-compare-item positive">AI remediation with effort & impact scoring</li>
              <li className="landing-compare-item positive">Vulnerability validation (XSS, SQLi, CORS, subdomain takeover...)</li>
              <li className="landing-compare-item positive">First scan results in 90 seconds</li>
              <li className="landing-compare-item positive">No sales process — start your trial now</li>
            </ul>
            <div className="landing-compare-price">
              <span className="landing-compare-price-tag positive">From $99/month</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== 6. SEE THE INTELLIGENCE (alternating screenshots) ===== */}
      <section className="landing-intelligence-section">
        <h2 className="section-title">Intelligence That Drives Action</h2>
        <p className="section-subtitle">Not just another list of CVEs. DriftAlarm shows you what matters, why it matters, and how to fix it.</p>

        {/* Section A: DriftAlarm Brief */}
        <Reveal className="intel-row" delay={0}>
          <div className="intel-screenshot">
            <ScreenshotFrame
              src="/images/screenshots/da-brief"
              alt="AI-generated security brief with CRITICAL threat level and 95% AI confidence score"
              size="full"
            />
          </div>
          <div className="intel-copy">
            <h3 className="intel-heading">AI-Generated Security Brief</h3>
            <p className="intel-text">
              Every scan produces an executive-ready threat assessment.
            </p>
            <ul className="intel-list">
              <li>Threat level with AI confidence score</li>
              <li>Plain-English executive summary — no jargon</li>
              <li>Misconfigurations identified and categorized</li>
              <li>Information leakage detection</li>
              <li>Attack complexity rating</li>
            </ul>
            <p className="intel-tagline">Share it with your board, your auditor, or your team. Your AI security analyst works 24/7.</p>
          </div>
        </Reveal>

        {/* Section B: MITRE ATT&CK */}
        <Reveal className="intel-row intel-row--reverse" delay={0}>
          <div className="intel-screenshot">
            <ScreenshotFrame
              src="/images/screenshots/mitre-attack-mapping"
              alt="8 MITRE ATT&CK techniques mapped including T1190 Exploit Public-Facing Application"
              size="full"
            />
          </div>
          <div className="intel-copy">
            <h3 className="intel-heading">See How Attackers Would Chain Your Vulnerabilities</h3>
            <p className="intel-text">
              Every finding is mapped to the MITRE ATT&CK framework — the same methodology used by nation-state threat intelligence teams.
            </p>
            <ul className="intel-list">
              <li>Initial Access vectors identified</li>
              <li>Lateral movement paths mapped</li>
              <li>Credential access risks flagged</li>
              <li>Persistence opportunities highlighted</li>
            </ul>
            <p className="intel-tagline">Understand not just what's vulnerable, but how an attacker would actually exploit it.</p>
          </div>
        </Reveal>

        {/* Section C: Prioritized Recommendations */}
        <Reveal className="intel-row" delay={0}>
          <div className="intel-screenshot">
            <ScreenshotFrame
              src="/images/screenshots/prioritized-recommendations"
              alt="4 ranked remediation recommendations with severity, effort, impact, and timeline"
              size="full"
            />
          </div>
          <div className="intel-copy">
            <h3 className="intel-heading">A Remediation Roadmap, Not a Data Dump</h3>
            <p className="intel-text">
              Every recommendation is ranked by:
            </p>
            <ol className="intel-list intel-list--numbered">
              <li>Severity (Critical → Low)</li>
              <li>Effort to fix (Low / Medium / High)</li>
              <li>Business impact (Critical → Medium)</li>
              <li>Timeline (Immediate / 24-48h / Week / Month)</li>
            </ol>
            <p className="intel-tagline">Each item includes the specific attack vector and exact remediation steps for your technology stack. Fix the right things in the right order.</p>
          </div>
        </Reveal>

        {/* Section D: Executive Report */}
        <Reveal className="intel-row intel-row--reverse" delay={0}>
          <div className="intel-screenshot">
            <div className="intel-pdf-preview">
              <div className="intel-pdf-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14,2 14,8 20,8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10,9 9,9 8,9" />
                </svg>
              </div>
              <span className="intel-pdf-title">Executive Security Posture Report</span>
              <span className="intel-pdf-subtitle">AI-Generated &bull; 2 Pages &bull; PDF</span>
              <a
                href="/downloads/driftalarm-executive-report-sample.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="intel-pdf-download"
                onClick={(e) => e.stopPropagation()}
              >
                Download Sample Report (PDF) →
              </a>
            </div>
          </div>
          <div className="intel-copy">
            <h3 className="intel-heading">Board-Ready Executive Reports, Generated Automatically</h3>
            <p className="intel-text">
              Every account gets AI-generated executive security posture reports you can share with your board, auditors, or leadership team.
            </p>
            <ul className="intel-list">
              <li>Overall security grade (A through F)</li>
              <li>Key risk indicators with business impact context</li>
              <li>Trend analysis showing posture changes over time</li>
              <li>Prioritized recommended actions with timelines</li>
              <li>Compliance gap assessment (CIS, OWASP, NIST)</li>
            </ul>
            <p className="intel-tagline">No more scrambling before board meetings. Your security story is always ready.</p>
          </div>
        </Reveal>

        {/* Section E: Live Drift Detection */}
        <Reveal className="intel-row intel-row--wide" delay={0}>
          <div className="intel-screenshot">
            <ScreenshotFrame
              src="/images/screenshots/drift-event-detail-v2"
              alt="Drift alarms list showing critical known-exploited vulnerabilities and end-of-life software detected on a monitored host"
              size="full"
              retina
            />
          </div>
          <div className="intel-copy">
            <h3 className="intel-heading">Every Change. Tracked. Actionable.</h3>
            <p className="intel-text">
              When something changes in your attack surface, DriftAlarm catches it and tells you exactly what happened:
            </p>
            <ul className="intel-list intel-list--events">
              <li><span className="event-severity event-severity--high">HIGH</span> Exposed MySQL Service (MySQL 5.7.33) detected</li>
              <li><span className="event-severity event-severity--medium">MEDIUM</span> 4 new technologies detected: MySQL, HSTS, Nginx</li>
              <li><span className="event-severity event-severity--medium">MEDIUM</span> New port opened: 47.94.238.108:22</li>
              <li><span className="event-severity event-severity--info">DRIFT</span> 1 negative change, 369 neutral changes</li>
            </ul>
            <p className="intel-tagline">Each event can be acknowledged, suppressed, snoozed, or resolved — so your team stays focused on what matters.</p>
          </div>
        </Reveal>
      </section>

      {/* ===== 7. PRICING PREVIEW ===== */}
      <section className="landing-pricing-section">
        <h2 className="section-title">Simple, Transparent Pricing</h2>
        <p className="section-subtitle">All features included at every tier. No feature gating — just choose your scale.</p>

        <div className="landing-pricing-grid">
          <Reveal className="landing-pricing-card landing-pricing-popular" delay={0}>
            <div className="landing-pricing-header">
              <h3 className="landing-pricing-tier">Standard</h3>
              <div className="landing-pricing-price">$99<span>/month</span></div>
              <div className="landing-pricing-annual">or $999/year (save $189)</div>
            </div>
            <ul className="landing-pricing-features">
              <li><span className="check">✓</span> 1 domain + 1 IP</li>
              <li><span className="check">✓</span> 10 Fast Scans per week</li>
              <li><span className="check">✓</span> 5 Deep Scans per week</li>
              <li><span className="check">✓</span> 3 Scheduled Scans per week</li>
              <li><span className="check">✓</span> Drift Detection &amp; Alerts</li>
              <li><span className="check">✓</span> AI Remediation Guidance</li>
              <li><span className="check">✓</span> All AI analysis features included</li>
            </ul>
            <button className="da-btn da-btn--primary da-btn--block" onClick={handleTrial}>
              <span className="cta-shiny-text">Start Free 30-Day Trial</span>
            </button>
          </Reveal>

          <Reveal className="landing-pricing-card" delay={120}>
            <div className="landing-pricing-header">
              <h3 className="landing-pricing-tier">Pro</h3>
              <div className="landing-pricing-price">$389<span>/month</span></div>
              <div className="landing-pricing-annual">or $3,899/year (save $769)</div>
            </div>
            <ul className="landing-pricing-features">
              <li><span className="check">✓</span> 5 domains + 10 subnets</li>
              <li><span className="check">✓</span> 50 Fast Scans per week</li>
              <li><span className="check">✓</span> 25 Deep Scans per week</li>
              <li><span className="check">✓</span> 15 Scheduled Scans per week</li>
              <li><span className="check">✓</span> Same features, 5x the scale</li>
              <li><span className="check">✓</span> AI Executive Reports</li>
              <li><span className="check">✓</span> Slack Notifications</li>
            </ul>
            <Link to="/upgrade" className="da-btn da-btn--secondary da-btn--block">
              View Pro Plans
            </Link>
          </Reveal>

          <Reveal className="landing-pricing-card" delay={240}>
            <div className="landing-pricing-header">
              <h3 className="landing-pricing-tier">Enterprise</h3>
              <div className="landing-pricing-price landing-pricing-price-contact">Custom</div>
            </div>
            <ul className="landing-pricing-features">
              <li><span className="check">✓</span> Custom domains &amp; IPs</li>
              <li><span className="check">✓</span> Unlimited scans</li>
              <li><span className="check">✓</span> Volume pricing for large perimeters</li>
              <li><span className="check">✓</span> Priority support</li>
              <li><span className="check">✓</span> API access</li>
              <li><span className="check">✓</span> Scheduled report delivery</li>
            </ul>
            <button className="da-btn da-btn--secondary da-btn--block" onClick={() => window.location.href = ENTERPRISE_SIZING_MAILTO}>
              Get a Quote
            </button>
          </Reveal>
        </div>

        <p className="landing-pricing-allfeatures">
          Every plan includes: AI Security Briefs &bull; MITRE ATT&CK Mapping &bull; Drift Detection (41 rules)
          &bull; Compliance Mapping (OWASP, CIS, NIST) &bull; Vulnerability Validation &bull; Prioritized Remediation
        </p>

        <div className="landing-pricing-footer">
          <Link to="/upgrade" className="see-all-plans">See full comparison →</Link>
        </div>
      </section>

      {/* ===== 8. FAQ SECTION ===== */}
      <section className="faq-section">
        <h2 className="section-title">Common Questions</h2>

        <Reveal className="faq-container" delay={0}>
          {faqData.map((faq, index) => (
            <div
              key={index}
              className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => toggleFaq(index)}
              >
                <span className="faq-question-text">{faq.question}</span>
                <span className="faq-toggle">{expandedFaq === index ? '−' : '+'}</span>
              </button>
              {expandedFaq === index && (
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </Reveal>
      </section>

      {/* ===== 9. SECURITY GUIDES (keep as-is) ===== */}
      <section className="landing-guides-section">
        <h2 className="section-title">Security Guides</h2>
        <p className="section-subtitle">Practical guides for IT teams defending their attack surface</p>
        <div className="landing-guides-grid">
          <Reveal as={Link} to="/guides/ai-vulnerability-threats" className="landing-guide-card" delay={0}>
            <span className="landing-guide-category">AI Threats</span>
            <h3>AI Vulnerability Discovery</h3>
            <p>AI models can now find 181 exploits where previous models found 2. What this means for your business.</p>
          </Reveal>
          <Reveal as={Link} to="/guides/patch-window-collapse" className="landing-guide-card" delay={80}>
            <span className="landing-guide-category">Threat Intelligence</span>
            <h3>The Patch Window Is Dead</h3>
            <p>Time-to-exploit dropped from 63 days to negative 7. Why continuous monitoring beats annual pentests.</p>
          </Reveal>
          <Reveal as={Link} to="/guides/ai-attack-readiness" className="landing-guide-card" delay={160}>
            <span className="landing-guide-category">Readiness</span>
            <h3>AI Attack Readiness Checklist</h3>
            <p>AI-enabled attacks up 89% YoY. A 10-point checklist to assess your readiness.</p>
          </Reveal>
          <Reveal as={Link} to="/guides/attack-surface-monitoring" className="landing-guide-card" delay={0}>
            <span className="landing-guide-category">Fundamentals</span>
            <h3>Attack Surface Monitoring</h3>
            <p>Scope, discover, analyze, and prioritize your external exposures with a P0-P4 rubric.</p>
          </Reveal>
          <Reveal as={Link} to="/guides/configuration-drift" className="landing-guide-card" delay={80}>
            <span className="landing-guide-category">Drift Detection</span>
            <h3>Configuration Drift</h3>
            <p>How DNS changes, new ports, and TLS degradation create security gaps attackers exploit.</p>
          </Reveal>
          <Reveal as={Link} to="/guides/attack-surface-checklist" className="landing-guide-card" delay={160}>
            <span className="landing-guide-category">Checklist</span>
            <h3>Attack Surface Checklist</h3>
            <p>Weekly, monthly, and quarterly monitoring checklist for IT teams of 1-5 people.</p>
          </Reveal>
        </div>
        <div className="landing-guides-footer">
          <Link to="/guides" className="see-all-plans">Browse all 19 guides →</Link>
        </div>
      </section>

      {/* ===== 10. FINAL CTA ===== */}
      <section className="landing-final-cta-section">
        <Reveal className="landing-final-cta-content" delay={0}>
          <h2 className="landing-final-cta-title">
            Your Attack Surface Changed While You Read This Page.
          </h2>
          <p className="landing-final-cta-subtitle">
            New subdomains. Expiring certificates. Open ports. Configuration drift.
            It's happening right now. The only question is whether you see it first — or an attacker does.
          </p>
          <button className="da-btn da-btn--primary da-btn--lg" onClick={handleTrial}>
            <span className="cta-text cta-shiny-text">Start Your Free 30-Day Trial</span>
            <span className="cta-icon">→</span>
          </button>
          <p className="landing-final-cta-secondary">
            <Link to="/how-it-works">or see how it works →</Link>
          </p>
          <p className="landing-final-cta-subtext">
            No credit card &bull; No sales call &bull; Full Standard access for 30 days
          </p>
        </Reveal>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}

export default LandingPage;
